import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  MultiSelect,
  Select,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Loader,
  Center,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconFilter,
  IconRefresh,
  IconScale,
  IconBuilding,
  IconClock,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  useQaReviewQueue,
  useQaReviewQueueStats,
} from "../../hooks/useWorkflows";
import { useExternalInstitutions } from "../../hooks/useSendOuts";
import { usePathologists } from "../../hooks/useReferenceData";
import { useAuthStore } from "../../store/auth.store";
import type { QaReviewQueueFilters } from "../../services/workflows.service";
import { QaReviewModal } from "./QaReviewDetailPage";

dayjs.extend(relativeTime);

// ==================== CONSTANTS ====================

const ITEMS_PER_PAGE = 20;

// Age filter options
const AGE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "1", label: "1+ days" },
  { value: "3", label: "3+ days" },
  { value: "7", label: "7+ days" },
  { value: "14", label: "14+ days" },
  { value: "30", label: "30+ days" },
];

// ==================== TYPES ====================

interface QaReviewFilters {
  institutionIds?: string[];
  originalPathologistIds?: string[];
  siteNames?: string[];
  patientSearch?: string;
  minDaysPending?: number;
}

// ==================== MAIN COMPONENT ====================

export default function QaReviewQueuePage() {
  // Modal states
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);

  // QA Review detail modal state
  const [selectedQaReviewId, setSelectedQaReviewId] = useState<string | null>(
    null
  );
  const [qaModalOpened, { open: openQaModal, close: closeQaModal }] =
    useDisclosure(false);

  // Page state
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<QaReviewFilters>({});
  const [pendingFilters, setPendingFilters] = useState<QaReviewFilters>({});
  const [page, setPage] = useState(1);

  // Auth - get current user for filtering
  const currentUserId = useAuthStore((state) => state.profile?.id);
  const currentUserRoleName = useAuthStore((state) => state.profile?.roleName);

  // Determine if current user is a pathologist (QA reviewer)
  const isPathologist = currentUserRoleName === "Pathologist";
  const reviewerIdForFilter = isPathologist ? currentUserId : undefined;

  // Build API filters
  const queueFilters: QaReviewQueueFilters = useMemo(() => {
    return {
      reviewerId: reviewerIdForFilter,
      institutionIds: filters.institutionIds,
      search: debouncedSearch || undefined,
    };
  }, [reviewerIdForFilter, filters.institutionIds, debouncedSearch]);

  // Data hooks
  const {
    data: queueItems = [],
    isLoading,
    isFetching,
    refetch,
  } = useQaReviewQueue(queueFilters);
  const { data: stats } = useQaReviewQueueStats(reviewerIdForFilter);
  const { data: institutions = [] } = useExternalInstitutions();
  const { data: pathologists = [] } = usePathologists();

  // Institution options for filter (sorted alphabetically)
  const institutionOptions = useMemo(() => {
    return institutions
      .filter((inst) => inst.isActive)
      .map((inst) => ({
        value: inst.id,
        label: inst.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [institutions]);

  // Pathologist options for filter (sorted alphabetically)
  const pathologistOptions = useMemo(() => {
    return pathologists
      .map((p) => ({
        value: p.id,
        label: p.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [pathologists]);

  // Site options for filter (derived from queue items, sorted alphabetically)
  const siteOptions = useMemo(() => {
    const uniqueSites = new Set(
      queueItems.map((item) => item.siteName).filter(Boolean)
    );
    return Array.from(uniqueSites)
      .map((site) => ({ value: site, label: site }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [queueItems]);

  // Apply client-side filters (backend handles institution and search)
  const filteredItems = useMemo(() => {
    let items = queueItems;

    // Filter by original pathologist
    if (filters.originalPathologistIds?.length) {
      items = items.filter(
        (item) =>
          item.originalPathologistId &&
          filters.originalPathologistIds?.includes(item.originalPathologistId)
      );
    }

    // Filter by site name
    if (filters.siteNames?.length) {
      items = items.filter((item) =>
        filters.siteNames?.includes(item.siteName)
      );
    }

    // Filter by patient name (case-insensitive search)
    if (filters.patientSearch?.trim()) {
      const searchLower = filters.patientSearch.toLowerCase().trim();
      items = items.filter((item) =>
        item.patientName.toLowerCase().includes(searchLower)
      );
    }

    // Filter by minimum days pending (age)
    if (filters.minDaysPending && filters.minDaysPending > 0) {
      items = items.filter(
        (item) => item.daysPending >= (filters.minDaysPending ?? 0)
      );
    }

    return items;
  }, [
    queueItems,
    filters.originalPathologistIds,
    filters.siteNames,
    filters.patientSearch,
    filters.minDaysPending,
  ]);

  // Pagination
  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
  const paginatedItems = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredItems.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredItems, page]);

  // Filter management
  const hasActiveFilters =
    (filters.institutionIds?.length ?? 0) > 0 ||
    (filters.originalPathologistIds?.length ?? 0) > 0 ||
    (filters.siteNames?.length ?? 0) > 0 ||
    !!filters.patientSearch?.trim() ||
    (filters.minDaysPending ?? 0) > 0;

  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPendingFilters({});
    setPage(1);
  };

  // Open QA review detail modal
  const handleOpenQaReview = (qaReviewId: string) => {
    setSelectedQaReviewId(qaReviewId);
    openQaModal();
  };

  // Close QA review detail modal
  const handleCloseQaModal = () => {
    closeQaModal();
    setSelectedQaReviewId(null);
  };

  // Get urgency color based on days pending
  const getUrgencyColor = (daysPending: number): string => {
    if (daysPending >= 7) return "red";
    if (daysPending >= 3) return "orange";
    return "gray";
  };

  // Get age filter label for display
  const getAgeFilterLabel = (minDays: number): string => {
    const option = AGE_OPTIONS.find((o) => o.value === String(minDays));
    return option?.label || `${minDays}+ days`;
  };

  // ==================== RENDER ====================

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header with Stats Chips */}
          <Group justify="space-between" align="center">
            <Group gap="md" align="center">
              <Group gap="xs">
                <ThemeIcon size="lg" radius="md" variant="light" color="cyan">
                  <IconScale size={20} />
                </ThemeIcon>
                <Title order={2}>QA Review Queue</Title>
              </Group>
              <Text c="dimmed" size="sm">
                External opinion QA reviews awaiting comparison
              </Text>
            </Group>

            {/* Stats as Chips */}
            <Group gap="xs">
              <Badge
                size="lg"
                variant="light"
                color="cyan"
                leftSection={<IconScale size={14} />}
              >
                {stats?.total ?? 0} Pending
              </Badge>
              {stats?.byInstitution.map((inst) => (
                <Badge
                  key={inst.institutionId}
                  size="lg"
                  variant="light"
                  color="blue"
                  leftSection={<IconBuilding size={14} />}
                >
                  {inst.count} {inst.institutionName}
                </Badge>
              ))}
            </Group>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={24} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by case number..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.currentTarget.value);
                    setPage(1);
                  }}
                />
                <Text size="sm" c="dimmed">
                  Search by case number to find specific QA reviews
                </Text>
              </Stack>
            </Paper>

            {/* Queue Filters */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={24} />
                    <Text fw={500}>Queue Filters</Text>
                  </Group>
                  <ActionIcon
                    variant="subtle"
                    size="lg"
                    onClick={() => refetch()}
                    loading={isFetching}
                  >
                    <IconRefresh size={18} />
                  </ActionIcon>
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" fw={500} c="dimmed">
                      Active Filters:
                    </Text>
                    {(filters.institutionIds?.length ?? 0) > 0 && (
                      <Badge variant="light" color="blue">
                        {filters.institutionIds?.length} Institution
                        {(filters.institutionIds?.length ?? 0) > 1 ? "s" : ""}
                      </Badge>
                    )}
                    {(filters.originalPathologistIds?.length ?? 0) > 0 && (
                      <Badge variant="light" color="violet">
                        {filters.originalPathologistIds?.length} Pathologist
                        {(filters.originalPathologistIds?.length ?? 0) > 1
                          ? "s"
                          : ""}
                      </Badge>
                    )}
                    {(filters.siteNames?.length ?? 0) > 0 && (
                      <Badge variant="light" color="teal">
                        {filters.siteNames?.length} Site
                        {(filters.siteNames?.length ?? 0) > 1 ? "s" : ""}
                      </Badge>
                    )}
                    {filters.patientSearch?.trim() && (
                      <Badge variant="light" color="pink">
                        Patient: {filters.patientSearch}
                      </Badge>
                    )}
                    {(filters.minDaysPending ?? 0) > 0 && (
                      <Badge variant="light" color="orange">
                        {getAgeFilterLabel(filters.minDaysPending ?? 0)}
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-sm"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  Showing {filteredItems.length} QA review
                  {filteredItems.length !== 1 ? "s" : ""}
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table */}
          <Paper p="md" withBorder>
            {/* Pagination at top */}
            {totalPages > 1 && (
              <Group justify="flex-end" mb="md">
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={totalPages}
                  size="sm"
                />
              </Group>
            )}

            {isLoading ? (
              <Center py="xl">
                <Loader />
              </Center>
            ) : filteredItems.length === 0 ? (
              <Center py="xl">
                <Stack align="center" gap="xs">
                  <ThemeIcon
                    size="xl"
                    radius="xl"
                    variant="light"
                    color="green"
                  >
                    <IconScale size={24} />
                  </ThemeIcon>
                  <Text size="lg" fw={500}>
                    No pending QA reviews
                  </Text>
                  <Text size="sm" c="dimmed">
                    {hasActiveFilters
                      ? "No reviews match the current filters"
                      : "All external opinion QA reviews have been completed"}
                  </Text>
                </Stack>
              </Center>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Case #</Table.Th>
                    <Table.Th>Institution / Site</Table.Th>
                    <Table.Th>Specimen</Table.Th>
                    <Table.Th>Patient</Table.Th>
                    <Table.Th>Original Pathologist</Table.Th>
                    <Table.Th>Report Received</Table.Th>
                    <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {paginatedItems.map((item) => (
                    <Table.Tr key={item.id}>
                      <Table.Td style={{ minWidth: 120, whiteSpace: "nowrap" }}>
                        <Text fw={500}>{item.caseNumber}</Text>
                        <Group gap={4} mt={2}>
                          <Badge
                            size="xs"
                            color="cyan"
                            variant="filled"
                            leftSection={<IconScale size={10} />}
                          >
                            QA
                          </Badge>
                          {item.status === "in_review" && (
                            <Badge size="xs" color="yellow" variant="light">
                              In Review
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{item.institutionName || "—"}</Text>
                        {item.siteName && (
                          <Text size="xs" c="dimmed">
                            {item.siteName}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={2} style={{ maxWidth: 220 }}>
                          {item.specimenDescription || "—"}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{item.patientName || "—"}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {item.originalPathologistName || "—"}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {item.externalReportReceivedAt ? (
                          <Tooltip
                            label={dayjs(item.externalReportReceivedAt).format(
                              "MMM D, YYYY h:mm A"
                            )}
                          >
                            <Text size="sm">
                              {dayjs(item.externalReportReceivedAt).fromNow()}
                            </Text>
                          </Tooltip>
                        ) : (
                          <Text size="sm" c="dimmed">
                            —
                          </Text>
                        )}
                        <Badge
                          size="xs"
                          color={getUrgencyColor(item.daysPending)}
                          variant="light"
                          leftSection={<IconClock size={10} />}
                        >
                          {item.daysPending} day
                          {item.daysPending !== 1 ? "s" : ""}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Button
                          size="xs"
                          onClick={() => handleOpenQaReview(item.id)}
                        >
                          Review
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}

            {/* Pagination at bottom */}
            {totalPages > 1 && (
              <Group justify="flex-end" mt="md">
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={totalPages}
                  size="sm"
                />
              </Group>
            )}
          </Paper>
        </Stack>
      </Box>

      {/* Filter Modal */}
      <Modal
        opened={filterModalOpened}
        onClose={closeFilterModal}
        closeOnClickOutside={false}
        title="Filter QA Reviews"
        size="md"
      >
        <Stack gap="md">
          <MultiSelect
            label="External Institution"
            placeholder="Select institutions"
            data={institutionOptions}
            value={pendingFilters.institutionIds || []}
            onChange={(value) =>
              setPendingFilters((prev) => ({
                ...prev,
                institutionIds: value.length > 0 ? value : undefined,
              }))
            }
            searchable
            clearable
          />

          <MultiSelect
            label="Original Pathologist"
            placeholder="Select pathologists"
            data={pathologistOptions}
            value={pendingFilters.originalPathologistIds || []}
            onChange={(value) =>
              setPendingFilters((prev) => ({
                ...prev,
                originalPathologistIds: value.length > 0 ? value : undefined,
              }))
            }
            searchable
            clearable
          />

          <MultiSelect
            label="Site"
            placeholder="Select sites"
            data={siteOptions}
            value={pendingFilters.siteNames || []}
            onChange={(value) =>
              setPendingFilters((prev) => ({
                ...prev,
                siteNames: value.length > 0 ? value : undefined,
              }))
            }
            searchable
            clearable
          />

          <TextInput
            label="Patient"
            placeholder="Search by patient name"
            value={pendingFilters.patientSearch || ""}
            onChange={(e) =>
              setPendingFilters((prev) => ({
                ...prev,
                patientSearch: e.target.value || undefined,
              }))
            }
          />

          <Select
            label="Age (Days Pending)"
            placeholder="Select minimum age"
            data={AGE_OPTIONS}
            value={
              pendingFilters.minDaysPending
                ? String(pendingFilters.minDaysPending)
                : "all"
            }
            onChange={(value) =>
              setPendingFilters((prev) => ({
                ...prev,
                minDaysPending:
                  value && value !== "all" ? parseInt(value, 10) : undefined,
              }))
            }
            clearable
          />

          <Group justify="flex-end" gap="xs" mt="md">
            <Button variant="default" onClick={closeFilterModal}>
              Cancel
            </Button>
            <Button onClick={handleApplyFilters}>Apply Filters</Button>
          </Group>
        </Stack>
      </Modal>

      {/* QA Review Detail Modal */}
      <QaReviewModal
        opened={qaModalOpened}
        onClose={handleCloseQaModal}
        qaReviewId={selectedQaReviewId}
      />
    </Container>
  );
}
